import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Megaphone, MapPin, Mail, MessageCircle, Save, Info, Activity, CreditCard, BellRing, Send } from "lucide-react";

const ALERT_SOURCES = [
  ["reservation", "Nouvelle réservation"],
  ["waitlist", "Inscription en liste d'attente"],
  ["contact", "Message de contact"],
];

const EMPTY = {
  meta_pixel_id: "",
  google_ads_id: "",
  tiktok_pixel_id: "",
  google_ads_conversion_label: "",
  google_ads_lead_conversion_label: "",
  pinterest_tag_id: "",
  clarity_id: "",
  prospect_alert_email_enabled: true,
  prospect_alert_emails: "",
  prospect_alert_whatsapp_enabled: false,
  prospect_alert_whatsapp_phone: "",
  prospect_alert_sources: ["reservation", "waitlist", "contact"],
  ipgeo_api_key: "",
  smtp_host: "",
  smtp_port: "",
  smtp_user: "",
  smtp_password: "",
  smtp_from: "",
  smtp_tls: true,
  n8n_email_webhook_url: "",
  n8n_email_method: "GET",
  n8n_whatsapp_webhook_url: "",
  n8n_whatsapp_method: "GET",
  stripe_secret_key: "",
  stripe_webhook_secret: "",
};

function Field({ label, hint, testId, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-neutral-800">{label}</span>
      {hint && <span className="block text-xs text-neutral-500 mb-1.5">{hint}</span>}
      <input
        type={type}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
      />
    </label>
  );
}

function Section({ icon: Icon, title, desc, children }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-brand/10 p-2.5 text-brand">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-neutral-900">{title}</h2>
          {desc && <p className="mt-0.5 text-sm text-neutral-500">{desc}</p>}
        </div>
      </div>
      <div className="mt-5 grid gap-4">{children}</div>
    </div>
  );
}

export default function AdminSettings() {
  const [form, setForm] = useState(EMPTY);
  const [pwdSet, setPwdSet] = useState(false);
  const [stripeKeySet, setStripeKeySet] = useState(false);
  const [stripeWhSet, setStripeWhSet] = useState(false);
  const [stripeMode, setStripeMode] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/integrations");
      setForm({ ...EMPTY, ...data, smtp_password: "", stripe_secret_key: "", stripe_webhook_secret: "" });
      setPwdSet(!!data.smtp_password_set);
      setStripeKeySet(!!data.stripe_secret_key_set);
      setStripeWhSet(!!data.stripe_webhook_secret_set);
      setStripeMode(data.stripe_mode || "");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // ne pas envoyer les champs de lecture seule
      delete payload.smtp_password_set;
      delete payload.stripe_secret_key_set;
      delete payload.stripe_webhook_secret_set;
      delete payload.stripe_mode;
      delete payload.prospect_alert_emails_default;
      await api.put("/admin/integrations", payload);
      toast.success("Réglages enregistrés.");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleSource = (k) =>
    setForm((f) => {
      const cur = f.prospect_alert_sources || [];
      return { ...f, prospect_alert_sources: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] };
    });

  const testAlert = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/prospect-alert/test");
      const parts = [];
      (data.email || []).forEach((e) => parts.push(`Email ${e.to} : ${e.sent ? "envoyé" : `échec (${e.error})`}`));
      if (data.whatsapp) parts.push(`WhatsApp ${data.whatsapp.to} : ${data.whatsapp.sent ? "envoyé" : `échec (${data.whatsapp.error})`}`);
      const ok = (data.email || []).some((e) => e.sent) || data.whatsapp?.sent;
      (ok ? toast.success : toast.error)(parts.join(" · ") || "Aucun envoi");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8 max-w-4xl">
        <h1 className="text-3xl font-extrabold tracking-tighter" data-testid="admin-settings-title">
          Réglages &amp; Intégrations
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-500">
          Configurez ici toutes vos clés et intégrations, sans jamais toucher aux fichiers techniques.
          Les modifications sont prises en compte immédiatement.
        </p>

        <div className="mt-5 flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Les paramètres purement techniques (base de données, clé de sécurité) restent dans la
            configuration serveur pour des raisons de sécurité.
          </span>
        </div>

        <div className="mt-6 space-y-5">
          <Section
            icon={BellRing}
            title="Alerte nouveau prospect"
            desc="Sois prévenu dès qu'une réservation, une inscription en liste d'attente ou un message de contact arrive."
          >
            <div className="grid gap-2" data-testid="alert-sources">
              <span className="text-sm font-semibold text-neutral-800">Me prévenir pour</span>
              {ALERT_SOURCES.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    data-testid={`alert-source-${k}`}
                    checked={(form.prospect_alert_sources || []).includes(k)}
                    onChange={() => toggleSource(k)}
                  />
                  {label}
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
              <input
                type="checkbox"
                data-testid="alert-email-toggle"
                checked={!!form.prospect_alert_email_enabled}
                onChange={(e) => set("prospect_alert_email_enabled")(e.target.checked)}
              />
              <Mail className="h-4 w-4 text-brand" /> Alerte par email
            </label>
            {form.prospect_alert_email_enabled && (
              <Field
                label="Adresse(s) email"
                hint={`Plusieurs adresses possibles, séparées par des virgules. Vide = adresse admin (${form.prospect_alert_emails_default || "ADMIN_EMAIL"}). Utilise les réglages Emails ci-dessous.`}
                testId="alert-emails-input"
                value={form.prospect_alert_emails}
                onChange={set("prospect_alert_emails")}
                placeholder={form.prospect_alert_emails_default || "toi@exemple.fr"}
              />
            )}
            <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
              <input
                type="checkbox"
                data-testid="alert-whatsapp-toggle"
                checked={!!form.prospect_alert_whatsapp_enabled}
                onChange={(e) => set("prospect_alert_whatsapp_enabled")(e.target.checked)}
              />
              <MessageCircle className="h-4 w-4 text-emerald-600" /> Alerte WhatsApp
            </label>
            {form.prospect_alert_whatsapp_enabled && (
              <Field
                label="Numéro WhatsApp"
                hint="Ton numéro (ex : 06 12 34 56 78). Envoi via le webhook n8n WhatsApp configuré plus bas."
                testId="alert-whatsapp-input"
                value={form.prospect_alert_whatsapp_phone}
                onChange={set("prospect_alert_whatsapp_phone")}
                placeholder="06 12 34 56 78"
              />
            )}
            {form.prospect_alert_whatsapp_enabled && !form.n8n_whatsapp_webhook_url && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="alert-whatsapp-warning">
                {"Webhook n8n WhatsApp non renseigné : les alertes WhatsApp ne partiront pas tant qu'il n'est pas configuré (section WhatsApp)."}
              </p>
            )}
            <div>
              <button
                type="button"
                onClick={testAlert}
                disabled={testing}
                data-testid="alert-test-btn"
                className="inline-flex items-center gap-2 rounded-lg border border-brand/40 bg-brand/5 px-4 py-2 text-sm font-semibold text-brand hover:border-brand disabled:opacity-50"
              >
                <Send className="h-4 w-4" /> {testing ? "Envoi…" : "Envoyer une alerte test"}
              </button>
              <p className="mt-1 text-xs text-neutral-500">{"Enregistre d'abord tes réglages, puis envoie un test."}</p>
            </div>
          </Section>

          <Section
            icon={Megaphone}
            title="Publicité (pixels)"
            desc="Suivez vos conversions publicitaires. Ces identifiants sont injectés automatiquement sur le site public."
          >
            <Field
              label="Pixel Facebook / Meta"
              hint="Identifiant du pixel Meta (ex : 123456789012345)."
              testId="settings-meta_pixel_id-input"
              value={form.meta_pixel_id}
              onChange={set("meta_pixel_id")}
              placeholder="Meta Pixel ID"
            />
            <Field
              label="Google Ads / Analytics"
              hint="ID de mesure Google (ex : AW-123456789 ou G-XXXXXXX)."
              testId="settings-google_ads_id-input"
              value={form.google_ads_id}
              onChange={set("google_ads_id")}
              placeholder="AW-XXXXXXXXX / G-XXXXXXX"
            />
            <Field
              label="Google Ads : libellé de conversion « réservation terminée »"
              hint="Dans Google Ads → Objectifs → Conversions → votre conversion → Configurer la balise → « send_to » (ex : AW-123456789/AbCdEfGhIjK). Sans ce libellé, Google Ads ne compte pas les réservations."
              testId="settings-google_ads_conversion_label-input"
              value={form.google_ads_conversion_label}
              onChange={set("google_ads_conversion_label")}
              placeholder="AW-XXXXXXXXX/XXXXXXXXXXX"
            />
            <Field
              label="Google Ads : libellé de conversion « envoi de formulaire de lead »"
              hint="Conversion envoyée (valeur 1 €) à chaque demande envoyée depuis le formulaire élève. Laisser vide pour désactiver."
              testId="settings-google_ads_lead_conversion_label-input"
              value={form.google_ads_lead_conversion_label}
              onChange={set("google_ads_lead_conversion_label")}
              placeholder="AW-XXXXXXXXX/XXXXXXXXXXX"
            />
            <Field
              label="Pixel TikTok"
              hint="Identifiant du pixel TikTok (optionnel)."
              testId="settings-tiktok_pixel_id-input"
              value={form.tiktok_pixel_id}
              onChange={set("tiktok_pixel_id")}
              placeholder="TikTok Pixel ID"
            />
            <Field
              label="Tag Pinterest"
              hint="Identifiant du tag Pinterest (Gestionnaire de publicités → Conversions → Tag ID, 13 chiffres)."
              testId="settings-pinterest_tag_id-input"
              value={form.pinterest_tag_id}
              onChange={set("pinterest_tag_id")}
              placeholder="Pinterest Tag ID"
            />
            <p className="text-xs text-neutral-500" data-testid="settings-pixels-events-info">
              {"Fin de réservation (documents obligatoires déposés) envoyée automatiquement : Meta « Schedule », TikTok « Schedule », Google Ads « conversion » (avec le libellé ci-dessus), Pinterest « signup »."}
            </p>
          </Section>

          <Section
            icon={Activity}
            title="Microsoft Clarity"
            desc="Cartes de chaleur et enregistrements de session pour analyser le comportement des visiteurs. Collez simplement votre ID de projet."
          >
            <Field
              label="Clarity Project ID"
              hint="Dans clarity.microsoft.com → Settings → Overview (ex : abcde12345). Le script est injecté automatiquement après consentement cookies."
              testId="settings-clarity_id-input"
              value={form.clarity_id}
              onChange={set("clarity_id")}
              placeholder="Clarity Project ID"
            />
          </Section>

          <Section
            icon={MapPin}
            title="Géolocalisation par IP"
            desc="Détecte automatiquement la ville / région d'un visiteur à partir de son adresse IP."
          >
            <Field
              label="Clé API ipgeolocation.io"
              hint="Créez un compte gratuit sur ipgeolocation.io puis collez votre clé API ici."
              testId="settings-ipgeo_api_key-input"
              value={form.ipgeo_api_key}
              onChange={set("ipgeo_api_key")}
              placeholder="Clé API de géolocalisation"
            />
          </Section>

          <Section
            icon={Mail}
            title="Emails (SMTP)"
            desc="Serveur d'envoi des emails transactionnels (confirmations, notifications)."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Serveur SMTP"
                testId="settings-smtp_host-input"
                value={form.smtp_host}
                onChange={set("smtp_host")}
                placeholder="smtp.gmail.com"
              />
              <Field
                label="Port"
                testId="settings-smtp_port-input"
                value={form.smtp_port}
                onChange={set("smtp_port")}
                placeholder="587"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Identifiant (utilisateur)"
                testId="settings-smtp_user-input"
                value={form.smtp_user}
                onChange={set("smtp_user")}
                placeholder="utilisateur@domaine.fr"
              />
              <Field
                label="Mot de passe"
                type="password"
                hint={pwdSet ? "Déjà configuré — laissez vide pour ne pas changer." : "Mot de passe / clé d'application SMTP."}
                testId="settings-smtp_password-input"
                value={form.smtp_password}
                onChange={set("smtp_password")}
                placeholder={pwdSet ? "•••••••• (inchangé)" : "Mot de passe SMTP"}
              />
            </div>
            <Field
              label="Expéditeur (From)"
              hint="Nom et adresse affichés comme expéditeur."
              testId="settings-smtp_from-input"
              value={form.smtp_from}
              onChange={set("smtp_from")}
              placeholder="PlanPermis <no-reply@planpermis.fr>"
            />
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                data-testid="settings-smtp_tls-toggle"
                checked={!!form.smtp_tls}
                onChange={(e) => set("smtp_tls")(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-300 text-brand focus:ring-brand"
              />
              <span className="text-sm font-medium text-neutral-800">Utiliser le chiffrement TLS (recommandé)</span>
            </label>

            <div className="mt-2 border-t border-neutral-100 pt-4">
              <p className="text-sm font-semibold text-neutral-800">Alternative : webhook n8n (emails)</p>
              <p className="text-xs text-neutral-500 mb-3">
                Si renseigné, les emails passent par ce workflow n8n plutôt que par SMTP.
              </p>
              <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
                <Field
                  label="URL du webhook n8n"
                  testId="settings-n8n_email_webhook_url-input"
                  value={form.n8n_email_webhook_url}
                  onChange={set("n8n_email_webhook_url")}
                  placeholder="https://n8n.mondomaine.fr/webhook/email"
                />
                <label className="block">
                  <span className="block text-sm font-semibold text-neutral-800">Méthode</span>
                  <select
                    data-testid="settings-n8n_email_method-select"
                    value={form.n8n_email_method}
                    onChange={(e) => set("n8n_email_method")(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand focus:outline-none"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </select>
                </label>
              </div>
            </div>
          </Section>

          <Section
            icon={MessageCircle}
            title="WhatsApp (n8n)"
            desc="Notification WhatsApp envoyée aux auto-écoles lors de l'attribution d'un prospect."
          >
            <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
              <Field
                label="URL du webhook n8n"
                testId="settings-n8n_whatsapp_webhook_url-input"
                value={form.n8n_whatsapp_webhook_url}
                onChange={set("n8n_whatsapp_webhook_url")}
                placeholder="https://n8n.mondomaine.fr/webhook/whatsapp"
              />
              <label className="block">
                <span className="block text-sm font-semibold text-neutral-800">Méthode</span>
                <select
                  data-testid="settings-n8n_whatsapp_method-select"
                  value={form.n8n_whatsapp_method}
                  onChange={(e) => set("n8n_whatsapp_method")(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-brand focus:outline-none"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                </select>
              </label>
            </div>
          </Section>

          <Section
            icon={CreditCard}
            title="Paiement en ligne (Stripe)"
            desc="Encaissement des acomptes de réservation via Stripe Checkout. Les clés sont stockées de façon sécurisée et prennent effet immédiatement."
          >
            <div className="flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50 p-3 text-sm text-amber-800">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {stripeMode === "live"
                  ? "Mode LIVE actif : les paiements sont réels (cartes débitées)."
                  : stripeMode === "test"
                  ? "Mode TEST actif : aucun débit réel (clé sk_test_)."
                  : "Aucune clé Stripe configurée pour l'instant — la réservation payante est indisponible tant qu'une clé secrète n'est pas renseignée."}
              </span>
            </div>
            <Field
              label="Clé secrète Stripe"
              type="password"
              hint={
                stripeKeySet
                  ? "Déjà configurée — laissez vide pour ne pas changer. Format : sk_live_… (réel) ou sk_test_… (test)."
                  : "Dashboard Stripe → Développeurs → Clés API. Format : sk_live_… (réel) ou sk_test_… (test)."
              }
              testId="settings-stripe_secret_key-input"
              value={form.stripe_secret_key}
              onChange={set("stripe_secret_key")}
              placeholder={stripeKeySet ? "•••••••• (inchangée)" : "sk_live_… ou sk_test_…"}
            />
            <Field
              label="Secret du webhook (optionnel)"
              type="password"
              hint={
                stripeWhSet
                  ? "Déjà configuré — laissez vide pour ne pas changer. Crée un webhook vers …/api/stripe/webhook (événement checkout.session.completed)."
                  : "Facultatif mais recommandé. Crée un endpoint webhook vers …/api/stripe/webhook puis colle le secret whsec_… ici."
              }
              testId="settings-stripe_webhook_secret-input"
              value={form.stripe_webhook_secret}
              onChange={set("stripe_webhook_secret")}
              placeholder={stripeWhSet ? "•••••••• (inchangé)" : "whsec_…"}
            />
          </Section>
        </div>

        <div className="sticky bottom-0 mt-6 -mx-5 border-t border-neutral-200 bg-white/90 px-5 py-4 backdrop-blur sm:-mx-8 sm:px-8">
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-save-btn"
            className="inline-flex items-center gap-2 rounded-md bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? "Enregistrement…" : "Enregistrer les réglages"}
          </button>
        </div>
      </div>
    </Shell>
  );
}
